import { createAsyncThunk } from "@reduxjs/toolkit"
import {
  BASE_URL,
  getBaseEndpoint,
} from "../../../features/endpoints/endpointManager"
import { Practice, PracticeAgreement } from "../schema"
import axios from "axios"
import { RootState } from "../../../reduxStore/store"
import { practiceSlice } from "../model"
import { fileDetails, fetchB64DocxFile } from "../../CMS/fileService"
import { practiceLoaderSlice } from "./loader"
import { cloneDeep } from "lodash"
import { DataStatus } from "../../enums"
import { appRoutes, getRouteWithQueryParams } from "../../../routes"
import {
  DOCUSIGN_REDIRECT_QUERY_PARAM_KEY,
  DocusignRedirectOrigin,
} from "../../../features/shared/hooks/useDocusignRedirect"
import { getCurrentFormattedDateForESign } from "../helpers"
import { apiService } from "../../../features/shared/apiService"
import appInsightsLogger from "../../../features/logging/appInsightsLogger"

const getPracticAgreementEndpoint = (practiceId: string): string => {
  return (
    getBaseEndpoint() + `/practice/practices/${practiceId}/practiceagreement`
  )
}
const getKnoxKeeneAgreementEndpoint = (practiceId: string): string => {
  return (
    getBaseEndpoint() + `/practice/practices/${practiceId}/knoxkeeneagreement`
  )
}
const practiceAgreementSigningRequestEndpoint = (
  practiceId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/practices/${practiceId}/practiceagreement/signingrequest`
  )
}
const knoxKeeneAgreementSigningRequestEndpoint = (
  practiceId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/practices/${practiceId}/knoxkeeneagreement/signingrequest`
  )
}

export const getPracticeAgreementData = createAsyncThunk(
  "practiceProfile/practiceAgreement/getPracticeAgreementData",
  async (
    args: {
      practiceId: string
      setDataStatus?: (status: DataStatus) => void
    },
    { dispatch, getState },
  ) => {
    const { practiceId, setDataStatus } = args

    setDataStatus?.(DataStatus.SENDING)
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        practiceAgreementGetDataStatus: DataStatus.SENDING,
      }),
    )

    const payload: PracticeAgreement = {
      practiceAgreement: {
        signedFileId: null,
        signedOn: null,
        signedBy: null,
        fileStatusDisplayId: null,
        resetOn: null,
      },
      knoxKeeneAgreement: {
        signedFileId: null,
        signedOn: null,
        signedBy: null,
        fileStatusDisplayId: null,
        resetOn: null,
      },
      redirectUrl: "",
    }

    // get practice agreement data
    try {
      const PAUrl: string = getPracticAgreementEndpoint(practiceId)
      const PAResponse = await axios.get(PAUrl)

      if (PAResponse.status === 200) {
        const data = PAResponse.data
        payload.practiceAgreement.signedFileId = data.executedFileId || null
        payload.practiceAgreement.signedOn = data.lastUpdatedDateTime || null
        payload.practiceAgreement.signedBy = data.signedBy || null
        payload.practiceAgreement.fileStatusDisplayId = data.fileStatus || null
        payload.practiceAgreement.resetOn = data.resetDateTime || null
      }

      const KKUrl: string = getKnoxKeeneAgreementEndpoint(practiceId)
      const KKResponse = await axios.get(KKUrl)

      if (KKResponse.status === 200) {
        const data = KKResponse.data
        payload.knoxKeeneAgreement.signedFileId = data.executedFileId || null
        payload.knoxKeeneAgreement.signedOn = data.lastUpdatedDateTime || null
        payload.knoxKeeneAgreement.signedBy = data.signedBy || null
        payload.knoxKeeneAgreement.fileStatusDisplayId = data.fileStatus || null
        payload.knoxKeeneAgreement.resetOn = data.resetDateTime || null
      }
    } catch (error) {
      setDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAgreementGetDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }

    const state = getState() as RootState
    const updatedPractice: Practice = cloneDeep({
      ...state.practice.practice,
      practiceAgreement: {
        ...state.practice.practice.practiceAgreement,
        ...payload,
      },
    })
    dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
    setDataStatus?.(DataStatus.LOADED)
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        practiceAgreementGetDataStatus: DataStatus.LOADED,
      }),
    )
  },
)

interface DocumentSigningRequestThunkArgs {
  practiceId: string
  userName: string
  email: string
  clientId: string
  shouldUseCountyRates?: boolean
}
export const practiceAgreementSigningRequest = createAsyncThunk(
  "practiceProfile/practiceAgreement/docusign",
  async (
    {
      practiceId,
      userName,
      email,
      clientId,
      shouldUseCountyRates = true,
    }: DocumentSigningRequestThunkArgs,
    { dispatch, getState },
  ) => {
    const practiceAgreementString = await fetchB64DocxFile(
      fileDetails.practiceAgreement.staticUrl,
    )

    const redirectURL = getRouteWithQueryParams(
      BASE_URL.concat(appRoutes.practice.PRACTICE_AGREEMENT_ROUTE),
      {
        practiceId,
        [DOCUSIGN_REDIRECT_QUERY_PARAM_KEY]:
          DocusignRedirectOrigin.PRACTICE_AGREEMENT,
      },
    )

    const payload = {
      currentDate: getCurrentFormattedDateForESign(),
      documentBase64: practiceAgreementString,
      documentName: "PracticeAgreementDocument",
      documentType: "DOCX",
      documentId: crypto.randomUUID(),
      signerName: userName,
      signerEmail: email,
      signerClientId: clientId,
      redirectURL,
      shouldUseCountyRates,
    }
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        practiceAgreementSigningRequestDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      const url: string = practiceAgreementSigningRequestEndpoint(practiceId)
      const response = await axios.post(url, payload)

      if (response.status === 200) {
        const data = response.data
        const state = getState() as RootState
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          practiceAgreement: {
            ...state.practice.practice.practiceAgreement,
            redirectUrl: data.embeddedSigningURL,
          },
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      }

      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAgreementSigningRequestDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAgreementSigningRequestDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)

export const knoxKeeneAgreementSigningRequest = createAsyncThunk(
  "practiceProfile/knoxKeeneAgreement/docusign",
  async (
    {
      practiceId,
      userName,
      email,
      clientId,
      shouldUseCountyRates = false,
    }: DocumentSigningRequestThunkArgs,
    { dispatch, getState },
  ) => {
    const knoxKeeneString = await fetchB64DocxFile(
      fileDetails.knoxKeene.staticUrl,
    )

    const redirectURL = getRouteWithQueryParams(
      BASE_URL.concat(appRoutes.practice.PRACTICE_AGREEMENT_ROUTE),
      {
        practiceId,
        [DOCUSIGN_REDIRECT_QUERY_PARAM_KEY]: DocusignRedirectOrigin.KNOX_KEENE,
      },
    )

    const payload = {
      currentDate: getCurrentFormattedDateForESign(),
      documentBase64: knoxKeeneString,
      documentName: "KnoxKeeneAgreementDocument",
      documentType: "DOCX",
      documentId: crypto.randomUUID(),
      signerName: userName,
      signerEmail: email,
      signerClientId: clientId,
      redirectURL,
      shouldUseCountyRates,
    }
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        knoxKeeneSigningRequestDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      const url: string = knoxKeeneAgreementSigningRequestEndpoint(practiceId)
      const response = await axios.post(url, payload)

      if (response.status === 200) {
        const data = response.data
        const state = getState() as RootState
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          practiceAgreement: {
            ...state.practice.practice.practiceAgreement,
            redirectUrl: data.embeddedSigningURL,
          },
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      }

      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneSigningRequestDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneSigningRequestDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface RemoveKnoxKeenePracticeAgreementThunkArgs {
  practiceId: string
}

export const removeKnoxKeenePracticeAgreement = createAsyncThunk(
  "practiceAgreement/removeKnoxKeene",
  async (
    { practiceId }: RemoveKnoxKeenePracticeAgreementThunkArgs,
    { dispatch },
  ) => {
    try {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneDeleteDataStatus: DataStatus.SENDING,
        }),
      )
      const url: string = getKnoxKeeneAgreementEndpoint(practiceId)
      const response = await axios.delete(url)

      if (response.status === 204 || response.status === 200) {
        // US 20047 AC11: archiving the agreement and clearing the California virtual services
        // answer are one server-side transaction now, so there is no second call to make here.
        await dispatch(
          getPracticeAgreementData({
            practiceId,
          }),
        )
        await dispatch(refreshPracticeAgreementFlags(practiceId))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            knoxKeeneDeleteDataStatus: DataStatus.LOADED,
          }),
        )
      }
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneDeleteDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

/**
 * Pulls the practice-level agreement state back into the store after an action the practice
 * agreement page renders from: the stale flags decide whether the newest agreement is offered for
 * signature, and hasOnlineServicesInCalifornia is set server-side when a Knox-Keene is removed.
 */
const refreshPracticeAgreementFlags = createAsyncThunk(
  "practiceAgreement/refreshFlags",
  async (practiceId: string, { dispatch }) => {
    const partialPractice =
      await apiService.practice.get.PARTIAL_PRACTICE_BY_ID({ practiceId })
    if (!partialPractice) return
    dispatch(
      practiceSlice.actions.patchPractice({
        isPracticeAgreementStale: partialPractice.isPracticeAgreementStale,
        isKnoxKeeneStale: partialPractice.isKnoxKeeneStale,
        hasOnlineServicesInCalifornia:
          partialPractice.hasOnlineServicesInCalifornia,
      }),
    )
  },
)

interface ResetAgreementThunkArgs {
  practiceId: string
}

/**
 * US 20047 AC13-AC15. Affiliate Relations "Re-sign and Replace" for the Standard Practice Agreement.
 * The server marks the agreement stale and stamps the reset audit; the previously signed document
 * stays active so it remains downloadable until the replacement is signed.
 */
export const resetPracticeAgreement = createAsyncThunk(
  "practiceAgreement/resetPracticeAgreement",
  async ({ practiceId }: ResetAgreementThunkArgs, { dispatch }) => {
    try {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAgreementResetDataStatus: DataStatus.SENDING,
        }),
      )
      await axios.post(`${getPracticAgreementEndpoint(practiceId)}/reset`)

      await dispatch(getPracticeAgreementData({ practiceId }))
      await dispatch(refreshPracticeAgreementFlags(practiceId))
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAgreementResetDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceAgreementResetDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

/**
 * US 20047 AC13-AC15. Affiliate Relations "Re-sign and Replace" for the Knox-Keene agreement.
 */
export const resetKnoxKeeneAgreement = createAsyncThunk(
  "practiceAgreement/resetKnoxKeeneAgreement",
  async ({ practiceId }: ResetAgreementThunkArgs, { dispatch }) => {
    try {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneResetDataStatus: DataStatus.SENDING,
        }),
      )
      await axios.post(`${getKnoxKeeneAgreementEndpoint(practiceId)}/reset`)

      await dispatch(getPracticeAgreementData({ practiceId }))
      await dispatch(refreshPracticeAgreementFlags(practiceId))
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneResetDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          knoxKeeneResetDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)
